/**
 * Last-write-wins by REQUEST ORDER, for list pages that refetch as the operator types.
 *
 * The search pages await a fetch and assign the result straight into state. Debounce reduces how
 * often two requests overlap but cannot prevent it, and nothing makes responses arrive in the
 * order they were sent: a slow "Ana" resolving after a quick "Ana 101" writes its rows LAST, and
 * the list then shows results for a term the box no longer contains. The operator sees rows that
 * do not match what they typed — and retyping, which issues a fresh request that usually wins,
 * appears to "fix" it.
 *
 * Deliberately not an AbortController. Aborting cancels the HTTP request, which is a different
 * (and heavier) decision: it needs plumbing through the api layer, turns every superseded search
 * into a console error, and still leaves the ordering question for anything already in flight.
 * This is the minimal correct thing — the response still arrives, it just does not get to win.
 *
 * Usage mirrors the shape of the call site it guards:
 *
 *     const isStale = sequence.begin();
 *     const response = await condoApi.getPeople(...);
 *     if (isStale()) return;          // a newer search has started; drop this one
 *     setPeople(response.data.data);
 */

export interface RequestSequence {
  /**
   * Marks the start of a request and returns a predicate telling that request whether it has
   * since been superseded. Call it after every await, before touching state.
   */
  begin: () => () => boolean;
}

export function createRequestSequence(): RequestSequence {
  let latest = 0;

  return {
    begin: () => {
      const id = ++latest;
      return () => id !== latest;
    },
  };
}
